import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, Button, Chip, CircularProgress, IconButton, Tooltip, Typography, Avatar } from "@material-ui/core";
import { fade, makeStyles, useTheme } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import CloseIcon from "@material-ui/icons/Close";
import config from "../../config/environment";
import { normalizeDraftProposal, parseProposalFromText } from "../../features/agents/draftMapper";

/** Text area scrolls; + / send sit in a toolbar row */
const COMPOSER_TEXTAREA_MIN_PX = 132;
const COMPOSER_TEXTAREA_MAX_VH = 30;
const COMPOSER_TOOLBAR_HEIGHT = 44;

/** Fixed desktop copilot panel (one chat column: messages + composer). */
export const COPILOT_FIXED_DOCK_PANEL_HEIGHT_CSS = "calc(100dvh - 84px - 16px)";
const MAX_ATTACHMENT_TOTAL_CHARS = 90000;
const MAX_TEXT_FILE_CHARS = 80000;

/** Keep WS generate_agent_config under API Gateway size limits and avoid replaying huge goals. */
const COPILOT_HISTORY_MAX_MESSAGES = 8;
const COPILOT_HISTORY_MAX_CHARS_PER_MSG = 400;
const COPILOT_GOAL_MAX_CHARS_ON_WIRE = 6000;
const COPILOT_FIELD_VALUE_MAX_CHARS = 200;

const compactFieldsForWire = (fields) => {
  if (!Array.isArray(fields)) return [];
  return fields.map((f) => {
    if (!f || typeof f !== "object") return f;
    const name = f.name || f.title || "";
    const nameLower = String(name).toLowerCase();
    const val = f.value;
    const out = { name };
    if (f.search_hint) out.search_hint = String(f.search_hint).slice(0, 80);
    if (f.in) out.in = f.in;
    if (nameLower === "python_code" || nameLower === "code") {
      if (val != null && String(val).trim() !== "") {
        out.configured = true;
        out.value = "[configured — do not rewrite]";
        out.chars = String(val).length;
      }
      return out;
    }
    if (typeof val === "string" && val.length > COPILOT_FIELD_VALUE_MAX_CHARS) {
      out.value = `${val.slice(0, 120)}…`;
    } else if (val !== undefined && val !== null && val !== "") {
      out.value = val;
    }
    return out;
  });
};

const compactActionForWire = (a) => {
  if (!a || typeof a !== "object") return a;
  const fields = compactFieldsForWire(a.fields || a.config?.fields || []);
  return {
    action_id: a.action_id,
    type: a.type,
    label: a.label || a.action_label,
    app: a.app,
    action_key: a.action_key,
    action_label: a.action_label || a.label,
    app_label: a.app_label,
    app_icon: a.app_icon,
    url: a.url,
    fields,
    credential: a.credential || "",
    authname: a.authname || "",
    config: {
      app: a.config?.app || a.app,
      action: a.config?.action || a.action_key,
      credential: a.config?.credential || a.credential || "",
      authname: a.config?.authname || a.authname || "",
      fields,
    },
  };
};

const compactTriggerForWire = (t) => {
  if (!t || typeof t !== "object") return t;
  const fields = compactFieldsForWire(t.fields || []);
  return {
    trigger_id: t.trigger_id,
    app: t.app,
    type: t.type,
    trigger_key: t.trigger_key || t.type,
    trigger_label: t.trigger_label || t.label,
    app_label: t.app_label,
    fields,
    credential: t.credential || "",
    authname: t.authname || "",
  };
};

/** Strip python_code blobs and truncate goal so WS frames stay under ~128KB. */
const compactCurrentAgentForWire = (agent) => {
  if (!agent || typeof agent !== "object") return {};
  let goal = agent.goal || "";
  if (typeof goal === "string" && goal.length > COPILOT_GOAL_MAX_CHARS_ON_WIRE) {
    goal = `${goal.slice(0, COPILOT_GOAL_MAX_CHARS_ON_WIRE)}…`;
  }
  return {
    name: agent.name,
    goal,
    triggers: (agent.triggers || []).map(compactTriggerForWire),
    actions: (agent.actions || []).map(compactActionForWire),
    rules: agent.rules,
    policy: agent.policy,
  };
};

const compactConversationHistoryForWire = (messages) => {
  if (!Array.isArray(messages)) return [];
  return messages
    .filter((m) => m.role === "user" || m.role === "assistant")
    .map((m) => {
      let content = m.text || "";
      const lower = content.toLowerCase();
      if (
        lower.includes("update this agent's configuration") &&
        lower.includes("instructions:") &&
        content.length > COPILOT_HISTORY_MAX_CHARS_PER_MSG
      ) {
        const after = content.split(/Instructions:/i).pop() || "";
        content = `[refine] ${after.trim().slice(0, COPILOT_HISTORY_MAX_CHARS_PER_MSG - 10)}`;
      } else if (content.length > COPILOT_HISTORY_MAX_CHARS_PER_MSG) {
        content = `${content.slice(0, COPILOT_HISTORY_MAX_CHARS_PER_MSG)}…`;
      }
      return { role: m.role, content };
    })
    .slice(-COPILOT_HISTORY_MAX_MESSAGES);
};

/** Persisted copilot thread (user + assistant only), same idea as AgentChatWebSocket `agent_chat_history_*`. */
const COPILOT_LOCAL_STORAGE_MAX = 20;

/**
 * Survives remounts (e.g. mobile↔desktop layout swap on window resize). Without this,
 * `location.state.initialPrompt` re-fires a full agent-builder run every remount.
 */
const autoSentInitialPrompts = new Set();

const initialPromptAutoSendKey = (copilotStorageId, text) =>
  `${String(copilotStorageId || "").trim()}::${String(text || "").trim()}`;

const getCopilotStorageKey = (copilotStorageId) => {
  if (copilotStorageId == null || String(copilotStorageId).trim() === "") return null;
  return `agent_builder_copilot_${String(copilotStorageId).trim()}`;
};

const loadPersistedCopilotMessages = (copilotStorageId) => {
  const key = getCopilotStorageKey(copilotStorageId);
  if (!key || typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) return [];
    return parsed
      .filter((m) => m && (m.role === "user" || m.role === "assistant"))
      .map((m) => ({ role: m.role, text: String(m.text || m.content || "") }));
  } catch (err) {
    console.error("AgentDraftingAssistant: could not load copilot history", err);
    return [];
  }
};

const saveCopilotMessagesToStorage = (copilotStorageId, messagesToSave) => {
  const key = getCopilotStorageKey(copilotStorageId);
  if (!key || typeof window === "undefined") return;
  try {
    const flat = (messagesToSave || [])
      .filter((m) => m.role === "user" || m.role === "assistant")
      .map((m) => ({ role: m.role, text: m.text || "" }));
    const toSave =
      flat.length > COPILOT_LOCAL_STORAGE_MAX
        ? [flat[0], ...flat.slice(-(COPILOT_LOCAL_STORAGE_MAX - 1))]
        : flat;
    localStorage.setItem(key, JSON.stringify(toSave));
  } catch (err) {
    console.error("AgentDraftingAssistant: could not save copilot history", err);
  }
};

const sourceFaviconUrl = (source) => {
  if (source?.favicon) return source.favicon;
  const host = String(source?.host || "").trim();
  if (host) return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=32`;
  try {
    const h = new URL(String(source?.url || "")).hostname;
    return h ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(h)}&sz=32` : "";
  } catch (e) {
    return "";
  }
};

const ResearchSourceChips = ({ sources, align = "flex-end" }) => {
  const [expanded, setExpanded] = useState(false);
  const list = Array.isArray(sources) ? sources.filter((s) => s && s.url) : [];
  if (!list.length) return null;

  const COLLAPSED_COUNT = 3;
  const overflow = Math.max(0, list.length - COLLAPSED_COUNT);
  const visible = expanded || overflow === 0 ? list.slice(0, 10) : list.slice(0, COLLAPSED_COUNT);

  const renderChip = (source) => {
    const host = source.host || (() => {
      try {
        return new URL(source.url).hostname;
      } catch (e) {
        return "source";
      }
    })();
    const favicon = sourceFaviconUrl(source);
    return (
      <Tooltip key={source.url} title={source.title || source.url} placement="top">
        <Chip
          size="small"
          component="a"
          href={source.url}
          target="_blank"
          rel="noopener noreferrer"
          clickable
          avatar={
            favicon ? (
              <Avatar
                src={favicon}
                alt=""
                style={{ width: 18, height: 18, marginLeft: 4 }}
              />
            ) : undefined
          }
          label={host.replace(/^www\./, "")}
          style={{
            maxWidth: 160,
            height: 24,
            backgroundColor: "rgba(15, 23, 42, 0.04)",
            border: "1px solid rgba(15, 23, 42, 0.08)",
            fontSize: 11,
          }}
        />
      </Tooltip>
    );
  };

  // Collapsed: overlapping favicon stack + "+N more" (ChatGPT-style)
  if (!expanded && overflow > 0) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent={align}
        style={{ gap: 8, marginTop: 10 }}
      >
        <Box display="flex" alignItems="center" style={{ position: "relative", height: 24 }}>
          {list.slice(0, COLLAPSED_COUNT).map((source, i) => {
            const favicon = sourceFaviconUrl(source);
            const host = source.host || "source";
            return (
              <Tooltip key={source.url} title={source.title || source.url} placement="top">
                <Avatar
                  src={favicon || undefined}
                  alt={host}
                  component="a"
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    width: 22,
                    height: 22,
                    fontSize: 10,
                    border: "1.5px solid #fff",
                    boxShadow: "0 0 0 1px rgba(15,23,42,0.12)",
                    marginLeft: i === 0 ? 0 : -8,
                    zIndex: COLLAPSED_COUNT - i,
                    backgroundColor: "#f1f5f9",
                    cursor: "pointer",
                  }}
                >
                  {(host || "?").charAt(0).toUpperCase()}
                </Avatar>
              </Tooltip>
            );
          })}
        </Box>
        <Chip
          size="small"
          clickable
          onClick={() => setExpanded(true)}
          label={`+${overflow} more`}
          style={{
            height: 24,
            backgroundColor: "rgba(15, 23, 42, 0.06)",
            border: "1px solid rgba(15, 23, 42, 0.1)",
            fontSize: 11,
            fontWeight: 600,
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      flexWrap="wrap"
      alignItems="center"
      justifyContent={align}
      style={{ gap: 6, marginTop: 10 }}
    >
      {visible.map(renderChip)}
      {expanded && overflow > 0 ? (
        <Chip
          size="small"
          clickable
          onClick={() => setExpanded(false)}
          label="Show less"
          style={{
            height: 24,
            backgroundColor: "rgba(15, 23, 42, 0.04)",
            border: "1px solid rgba(15, 23, 42, 0.08)",
            fontSize: 11,
          }}
        />
      ) : null}
    </Box>
  );
};

/** Raw extended-thinking streams can be huge; tail cap while streaming, then summarize when done. */
const COPILOT_REASONING_STREAM_TAIL = 1400;
const COPILOT_REASONING_SUMMARY_MAX = 380;
const COPILOT_THINKING_STEP_MAX = 360;

const clipReasoningStreamBuffer = (prev, add) => {
  const next = `${prev || ""}${add || ""}`;
  if (next.length <= COPILOT_REASONING_STREAM_TAIL) return next;
  return `…\n${next.slice(-(COPILOT_REASONING_STREAM_TAIL - 2))}`;
};

/** Plain-language blurb for the copilot panel (not a full chain-of-thought). */
const summarizeCopilotReasoning = (raw) => {
  const t = String(raw || "")
    .trim()
    .replace(/[\t ]+/g, " ");
  if (!t) return "";
  if (t.length <= COPILOT_REASONING_SUMMARY_MAX) return t;
  const cut = t.slice(0, COPILOT_REASONING_SUMMARY_MAX);
  const dot = cut.lastIndexOf(". ");
  if (dot > 100) return `${cut.slice(0, dot + 1).trim()}…`;
  const sp = cut.lastIndexOf(" ");
  if (sp > 160) return `${cut.slice(0, sp).trim()}…`;
  return `${cut.trim()}…`;
};

const readAttachmentContent = (file) =>
  new Promise((resolve) => {
    const name = file.name || "file";
    const isText =
      (file.type && file.type.startsWith("text/")) ||
      /\.(txt|md|csv|json|yaml|yml|xml|log|tsv|html?|css|js|jsx|ts|tsx)$/i.test(name);
    if (file.size > 2 * 1024 * 1024) {
      resolve(`[File "${name}" is over 2 MB — not included. Use a smaller file or paste excerpts.]`);
      return;
    }
    if (isText) {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result || "").slice(0, MAX_TEXT_FILE_CHARS));
      r.onerror = () => resolve(`[Could not read ${name}]`);
      r.readAsText(file);
      return;
    }
    if (file.type && file.type.startsWith("image/") && file.size <= 400 * 1024) {
      const r = new FileReader();
      r.onload = () => {
        const dataUrl = String(r.result || "");
        const base64 = dataUrl.includes(",") ? dataUrl.split(",")[1] : dataUrl;
        resolve(
          `[Image ${file.type} filename="${name}" — base64 below for context]\n${base64.slice(0, 120000)}`
        );
      };
      r.onerror = () => resolve(`[Could not read image ${name}]`);
      r.readAsDataURL(file);
      return;
    }
    resolve(
      `[Non-text file attached: "${name}" (${file.type || "unknown"}, ${Math.round(file.size / 1024)} KB). Paste key details in the prompt if the agent needs this file's contents.]`
    );
  });

const useUnifiedDockTextareaStyles = makeStyles((theme) => ({
  textarea: {
    color: theme.palette.text.primary,
    "&::placeholder": {
      color: theme.palette.text.secondary,
      opacity: 0.9,
    },
  },
}));

const useThinkingPulseStyles = makeStyles((theme) => ({
  pulse: {
    color: theme.palette.text.secondary,
    fontStyle: "italic",
    fontWeight: 500,
    animation: "$thinkingPulse 1.15s ease-in-out infinite",
  },
  "@keyframes thinkingPulse": {
    "0%, 100%": { opacity: 0.32 },
    "50%": { opacity: 1 },
  },
}));

/** Drop trailing copilot wait bubbles before a final assistant line. */
const stripCopilotWaitTail = (messages) => {
  const trimmed = [...messages];
  while (trimmed.length) {
    const last = trimmed[trimmed.length - 1];
    if (
      last.role === "thinking" ||
      last.role === "thinking_pending" ||
      (last.role === "thinking_stream" &&
        !last.streamComplete &&
        !(last.reasoning || "").trim() &&
        !(last.output || "").trim()) ||
      (last.role === "thinking_stream" &&
        last.planningPlaceholders &&
        !(last.reasoning || "").trim() &&
        !(last.output || "").trim())
    ) {
      trimmed.pop();
    } else {
      break;
    }
  }
  return trimmed;
};

const withoutThinkingPending = (messages) => messages.filter((m) => m.role !== "thinking_pending");

/** User affirming a propose_plan turn → sniper build with approved_apps. */
const looksLikeBuildConfirmation = (text) => {
  const t = String(text || "").trim().toLowerCase();
  if (!t) return false;
  if (
    /^(yes|yep|yeah|yup|ok|okay|sure|correct|confirmed?|perfect|proceed)\b/.test(t)
  ) {
    return true;
  }
  return (
    t.includes("looks good") ||
    t.includes("sounds good") ||
    t.includes("go ahead") ||
    t.includes("build it") ||
    t.includes("do it") ||
    t.includes("that's right") ||
    t.includes("thats right") ||
    t.includes("please build")
  );
};

const trimAttachmentsForPayload = (attachments) => {
  let used = 0;
  const out = [];
  for (const a of attachments) {
    const c = String(a.content || "");
    const left = MAX_ATTACHMENT_TOTAL_CHARS - used;
    if (left <= 0) break;
    const slice = c.slice(0, left);
    out.push({ name: a.name, content: slice });
    used += slice.length;
  }
  return out;
};

const SYSTEM_PROMPT = `
You are drafting a deterministic Workload Agent configuration.
Output compact JSON with keys:
name, description, goal, triggers[], actions[], rules[], policy{}
`;

const heuristicProposal = (prompt, currentAgent) => {
  const lower = (prompt || "").toLowerCase();
  const wantsSlack = lower.includes("slack");
  const wantsSms = lower.includes("sms");
  const wantsInvoice = lower.includes("invoice");
  const actionType = wantsSms ? "send_sms" : wantsSlack ? "send_slack" : "run_ai_task";
  return normalizeDraftProposal({
    name: currentAgent?.name || "Drafted Agent",
    description: "Draft proposal generated locally (fallback mode).",
    goal: String(currentAgent?.goal || ""),
    triggers: [
      {
        source: "workflow_event",
        type: wantsInvoice ? "invoice_closed" : "job_created",
        filter: "",
        dedupe_key: "payload.id",
        enabled: true,
      },
      {
        source: "schedule",
        type: "tick_5m",
        filter: "",
        dedupe_key: "",
        enabled: true,
      },
    ],
    actions: [
      {
        type: actionType,
        label: "Primary response action",
        config: { channel: wantsSlack ? "#ops" : undefined },
      },
    ],
    rules: [
      {
        name: "Primary deterministic rule",
        when: "event",
        if: wantsInvoice
          ? "event.type == 'invoice_closed' && payload.balance_due > 0"
          : "event.type == 'job_created'",
        severity: "warn",
        cooldown_minutes: 10,
      },
    ],
    policy: {
      max_actions_per_hour: 30,
      max_notifications_per_hour: 30,
      requires_approval_for: actionType === "update_record" ? ["update_record"] : [],
      escalation_channels: ["slack:#ops"],
    },
  });
};

const AgentDraftingAssistant = forwardRef(({
  currentAgent,
  onApplyDraft,
  onApplySection,
  initialPrompt = "",
  /** Called once after the navigation seed prompt is consumed (sent or skipped as already sent). */
  onInitialPromptConsumed,
  onConnectionStatusChange,
  onBusyChange,
  /** Route `agentId` (or stable id) so copilot chat persists in localStorage per agent. */
  copilotStorageId,
  /** Single chat column: thread + composer visually joined (desktop fixed dock). */
  unifiedDock = false,
}, ref) => {
  const theme = useTheme();
  const unifiedTextareaClasses = useUnifiedDockTextareaStyles();
  const thinkingPulseClasses = useThinkingPulseStyles();
  const dockChrome = useMemo(() => {
    if (!unifiedDock) return null;
    const dark = theme.palette.type === "dark";
    return {
      threadBg: theme.palette.background.default,
      composerBg: theme.palette.background.paper,
      composerBorderTop: `1px solid ${theme.palette.divider}`,
      inputShellBg: dark ? theme.palette.background.default : "#fafafa",
      inputShellBorder: `1px solid ${dark ? theme.palette.divider : "rgba(0, 0, 0, 0.14)"}`,
      toolbarBg: dark ? theme.palette.action.hover : "#f5f5f5",
      toolbarBorderTop: `1px solid ${theme.palette.divider}`,
      attachBtnBg: dark ? fade(theme.palette.common.white, 0.08) : "rgba(0,0,0,0.06)",
      assistantBubbleBg: dark ? theme.palette.action.hover : "#fff",
      assistantBubbleColor: theme.palette.text.primary,
      assistantBubbleBorder: `1px solid ${theme.palette.divider}`,
      assistantBubbleShadow: dark ? "none" : "0 1px 3px rgba(0,0,0,0.08)",
      thinkingCaptionColor: theme.palette.text.secondary,
      thinkingPanelBg: dark ? fade(theme.palette.common.white, 0.06) : "rgba(0,0,0,0.035)",
      thinkingPanelBorder: `1px solid ${theme.palette.divider}`,
      thinkingBodyColor: theme.palette.text.primary,
      thinkingOutputColor: theme.palette.text.secondary,
      thinkingInlineBg: dark ? fade(theme.palette.common.white, 0.06) : "rgba(0,0,0,0.05)",
      thinkingInlineBorder: `1px dashed ${theme.palette.divider}`,
      draftCardBg: dark ? theme.palette.action.hover : "#fff",
      draftCardBorder: `1px solid ${fade(theme.palette.primary.main, dark ? 0.45 : 0.35)}`,
      draftCardShadow: dark ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
    };
  }, [unifiedDock, theme]);

  const [input, setInput] = useState("");
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(() =>
    Boolean(config.AGENTS_WS_ENDPOINT || config.WS_AGENT_ENDPOINT)
  );
  const [messages, setMessages] = useState(() => loadPersistedCopilotMessages(copilotStorageId));

  useLayoutEffect(() => {
    setMessages(loadPersistedCopilotMessages(copilotStorageId));
  }, [copilotStorageId]);

  useEffect(() => {
    saveCopilotMessagesToStorage(copilotStorageId, messages);
  }, [messages, copilotStorageId]);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const wsRef = useRef(null);
  const reconnectRef = useRef(0);
  const fileInputRef = useRef(null);
  const threadScrollRef = useRef(null);
  const thinkingStreamScrollRef = useRef(null);
  /** Slugs from last agent_intake propose_plan — sent with buildConfirmed on "yes". */
  const pendingApprovedAppsRef = useRef([]);
  /** When true, next layout scrolls thread to bottom (user just sent, or new assistant content). */
  const stickToBottomRef = useRef(true);
  const onBusyChangeRef = useRef(onBusyChange);
  onBusyChangeRef.current = onBusyChange;

  useEffect(() => {
    onBusyChangeRef.current?.(busy);
  }, [busy]);

  useEffect(() => {
    return () => {
      onBusyChangeRef.current?.(false);
    };
  }, []);

  const wsEndpoint = useMemo(
    () => config.AGENTS_WS_ENDPOINT || config.WS_AGENT_ENDPOINT,
    []
  );

  /** Seed textarea from navigation state only when that prompt changes (not when user clears after send). */
  const lastSeededInitialPrompt = useRef(undefined);
  useEffect(() => {
    if (initialPrompt === lastSeededInitialPrompt.current) return;
    lastSeededInitialPrompt.current = initialPrompt || undefined;
    if (initialPrompt) setInput(initialPrompt);
  }, [initialPrompt]);

  const initialPromptAutoSentRef = useRef(null);
  useEffect(() => {
    if (!(initialPrompt || "").trim()) initialPromptAutoSentRef.current = null;
  }, [initialPrompt]);

  useEffect(() => {
    if (!wsEndpoint) return () => {};
    let cancelled = false;
    const connect = () => {
      if (cancelled) return;
      setConnecting(true);
      try {
        const ws = new WebSocket(wsEndpoint);
        wsRef.current = ws;
        ws.onopen = () => {
          setConnecting(false);
          setConnected(true);
          reconnectRef.current = 0;
        };
        ws.onerror = () => {
          setConnected(false);
        };
        ws.onclose = () => {
          setConnected(false);
          setConnecting(false);
          const attempt = Math.min(5000, 500 * Math.pow(2, reconnectRef.current));
          reconnectRef.current += 1;
          setTimeout(connect, attempt);
        };
        ws.onmessage = (event) => {
          let data = null;
          try {
            data = JSON.parse(event.data);
          } catch (err) {
            return;
          }
          const eventType = data.type || data.messageType;
          if (eventType === "thinking") {
            let step = (data.step || data.content || "").trim() || "Thinking…";
            if (step.length > COPILOT_THINKING_STEP_MAX) {
              step = `${step.slice(0, COPILOT_THINKING_STEP_MAX)}…`;
            }
            if (data.stream) {
              stickToBottomRef.current = true;
              setMessages((prev) => {
                const base = withoutThinkingPending(prev);
                const last = base[base.length - 1];
                if (last && last.role === "thinking_stream") {
                  return [
                    ...base.slice(0, -1),
                    { ...last, reasoning: clipReasoningStreamBuffer(last.reasoning, step), streamComplete: false },
                  ];
                }
                return [...base, { role: "thinking_stream", reasoning: step, output: "", streamComplete: false }];
              });
            } else {
              stickToBottomRef.current = true;
              setMessages((prev) => {
                const base = withoutThinkingPending(prev);
                const last = base[base.length - 1];
                if (last && last.role === "thinking") {
                  return [...base.slice(0, -1), { role: "thinking", text: step }];
                }
                return [...base, { role: "thinking", text: step }];
              });
            }
          }
          if (eventType === "thinking_stream_start") {
            stickToBottomRef.current = true;
            setMessages((prev) => {
              const base = withoutThinkingPending(prev);
              const last = base[base.length - 1];
              if (last && last.role === "thinking_stream" && !last.streamComplete) {
                return base;
              }
              return [...base, { role: "thinking_stream", reasoning: "", output: "", streamComplete: false }];
            });
          }
          if (eventType === "thinking_delta") {
            const chunk = data.chunk ?? "";
            const phase =
              data.phase === "output"
                ? "output"
                : data.phase === "status"
                  ? "status"
                  : "reasoning";
            stickToBottomRef.current = true;
            setMessages((prev) => {
              const base = withoutThinkingPending(prev);
              const last = base[base.length - 1];
              // Catalog/docs prep: keep a calm placeholder — do not dump ops bullets.
              if (phase === "status") {
                if (last && last.role === "thinking_stream" && !last.streamComplete) {
                  if (last.reasoning || last.output) return base;
                  return [
                    ...base.slice(0, -1),
                    { ...last, planningPlaceholders: true, streamComplete: false },
                  ];
                }
                return [
                  ...base,
                  {
                    role: "thinking_stream",
                    reasoning: "",
                    output: "",
                    planningPlaceholders: true,
                    streamComplete: false,
                  },
                ];
              }
              if (last && last.role === "thinking_stream") {
                if (phase === "output") {
                  return [
                    ...base.slice(0, -1),
                    {
                      ...last,
                      output: (last.output || "") + chunk,
                      planningPlaceholders: false,
                      streamComplete: false,
                    },
                  ];
                }
                return [
                  ...base.slice(0, -1),
                  {
                    ...last,
                    reasoning: clipReasoningStreamBuffer(last.reasoning, chunk),
                    planningPlaceholders: false,
                    streamComplete: false,
                  },
                ];
              }
              return [
                ...base,
                {
                  role: "thinking_stream",
                  reasoning: phase === "output" ? "" : chunk,
                  output: phase === "output" ? chunk : "",
                  planningPlaceholders: false,
                  streamComplete: false,
                },
              ];
            });
          }
          if (eventType === "thinking_stream_end") {
            setMessages((prev) => {
              const last = prev[prev.length - 1];
              if (last && last.role === "thinking_stream") {
                const full = (last.reasoning || "").trim();
                const summary = summarizeCopilotReasoning(full);
                const reasoningFull = full.length > summary.length + 8 ? full : undefined;
                return [
                  ...prev.slice(0, -1),
                  {
                    ...last,
                    streamComplete: true,
                    reasoning: reasoningFull ? summary : full,
                    reasoningFull,
                    thinkingDetailsOpen: false,
                  },
                ];
              }
              return prev;
            });
          }
          if (eventType === "research_sources") {
            const incoming = Array.isArray(data.sources) ? data.sources : [];
            if (!incoming.length) return;
            stickToBottomRef.current = true;
            setMessages((prev) => {
              const base = withoutThinkingPending(prev);
              const last = base[base.length - 1];
              const mergeSources = (existing = []) => {
                const seen = new Set(
                  (existing || []).map((s) => String(s?.url || "").replace(/\/$/, "").toLowerCase())
                );
                const next = [...(existing || [])];
                incoming.forEach((s) => {
                  const key = String(s?.url || "").replace(/\/$/, "").toLowerCase();
                  if (!key || seen.has(key)) return;
                  seen.add(key);
                  next.push(s);
                });
                return next.slice(0, 16);
              };
              if (last && last.role === "thinking_stream") {
                return [
                  ...base.slice(0, -1),
                  { ...last, sources: mergeSources(last.sources), streamComplete: false },
                ];
              }
              return [
                ...base,
                {
                  role: "thinking_stream",
                  reasoning: "",
                  output: "",
                  sources: mergeSources([]),
                  streamComplete: false,
                },
              ];
            });
          }
          if (eventType === "agent_intake") {
            const status = String(data.status || "").toLowerCase();
            const apps = Array.isArray(data.approved_apps)
              ? data.approved_apps
              : Array.isArray(data.proposed_apps)
                ? data.proposed_apps
                    .map((a) => (typeof a === "string" ? a : a?.slug || a?.app))
                    .filter(Boolean)
                : [];
            if (status === "propose_plan" && apps.length) {
              pendingApprovedAppsRef.current = apps.map((s) => String(s));
            } else if (status === "need_clarification") {
              // Keep prior approved apps if any; questions may refine them.
            }
            const msg = String(data.assistant_message || "").trim() || (
              status === "need_clarification"
                ? "I need a bit more detail before I can build this."
                : "Does this plan look right?"
            );
            stickToBottomRef.current = true;
            setMessages((prev) => [
              ...stripCopilotWaitTail(prev),
              { role: "assistant", text: msg, intakeStatus: status },
            ]);
            setBusy(false);
          }
          if (eventType === "agent_config") {
            pendingApprovedAppsRef.current = [];
            const proposal = data.proposal;
            const normalized = proposal ? normalizeDraftProposal(proposal) : null;
            if (normalized) setDraft(normalized);
            const finalSources = Array.isArray(data.research_sources) ? data.research_sources : null;
            stickToBottomRef.current = true;
            setMessages((prev) => {
              const trimmed = stripCopilotWaitTail(prev);
              let next = trimmed;
              if (finalSources && finalSources.length) {
                const lastThinkingIdx = [...trimmed]
                  .map((m, i) => (m && m.role === "thinking_stream" ? i : -1))
                  .filter((i) => i >= 0)
                  .pop();
                if (lastThinkingIdx != null) {
                  next = trimmed.map((m, i) =>
                    i === lastThinkingIdx
                      ? {
                          ...m,
                          sources: (() => {
                            const seen = new Set(
                              (m.sources || []).map((s) =>
                                String(s?.url || "").replace(/\/$/, "").toLowerCase()
                              )
                            );
                            const merged = [...(m.sources || [])];
                            finalSources.forEach((s) => {
                              const key = String(s?.url || "").replace(/\/$/, "").toLowerCase();
                              if (!key || seen.has(key)) return;
                              seen.add(key);
                              merged.push(s);
                            });
                            return merged.slice(0, 16);
                          })(),
                        }
                      : m
                  );
                }
              }
              return [
                ...next,
                {
                  role: "assistant",
                  text: normalized
                    ? "Generated agent config from your instructions. Review the draft card below — it’s applied to the form."
                    : "No proposal returned.",
                },
              ];
            });
            setBusy(false);
          }
          if (eventType === "agent_config_error") {
            stickToBottomRef.current = true;
            setMessages((prev) => {
              const trimmed = stripCopilotWaitTail(prev);
              const t = trimmed[trimmed.length - 1];
              if (t && t.role === "thinking_stream") {
                const full = (t.reasoning || "").trim();
                const summary = summarizeCopilotReasoning(full);
                const reasoningFull = full.length > summary.length + 8 ? full : undefined;
                trimmed[trimmed.length - 1] = {
                  ...t,
                  streamComplete: true,
                  reasoning: reasoningFull ? summary : full,
                  reasoningFull,
                  thinkingDetailsOpen: false,
                };
              }
              return [...trimmed, { role: "assistant", text: `Error: ${data.error || "Request failed."}` }];
            });
            setBusy(false);
          }
          if (eventType === "content") {
            setMessages((prev) => [...prev, { role: "assistant", text: data.content || "" }]);
            const parsed = parseProposalFromText(data.content || "");
            const normalized = normalizeDraftProposal(parsed);
            if (normalized) setDraft(normalized);
          }
          if (
            eventType === "agent_draft_created" ||
            eventType === "agent_rules_proposed" ||
            eventType === "agent_started" ||
            eventType === "agent_error"
          ) {
            if (data.proposal) {
              const normalized = normalizeDraftProposal(data.proposal);
              if (normalized) setDraft(normalized);
            }
            if (data.message) {
              setMessages((prev) => [...prev, { role: "assistant", text: data.message }]);
            }
          }
          if (eventType === "done") {
            setBusy(false);
          }
        };
      } catch (err) {
        setConnecting(false);
        setConnected(false);
      }
    };
    connect();
    return () => {
      cancelled = true;
      if (wsRef.current) wsRef.current.close();
    };
  }, [wsEndpoint]);

  const connectionStatusCbRef = useRef(onConnectionStatusChange);
  connectionStatusCbRef.current = onConnectionStatusChange;

  useEffect(() => {
    const cb = connectionStatusCbRef.current;
    if (!cb) return;
    if (!wsEndpoint) {
      cb("offline");
      return;
    }
    if (connected) cb("connected");
    else if (connecting) cb("connecting");
    else cb("disconnected");
  }, [connected, connecting, wsEndpoint]);

  const handleFileInputChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    for (const file of files) {
      const id = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}_${file.name}`;
      const content = await readAttachmentContent(file);
      setAttachments((prev) => [...prev, { id, name: file.name, content }]);
    }
  };

  const runAgentBuilderSend = useCallback(
    (userTextRaw, attPayload = []) => {
      const userText = (userTextRaw || "").trim();
      if (!userText && attPayload.length === 0) return;
      const userLine =
        userText +
        (attPayload.length ? `\n[${attPayload.length} file(s) attached for context]` : "");
      stickToBottomRef.current = true;
      const willUseWs = connected && wsRef.current && wsRef.current.readyState === 1;
      setMessages((prev) => [
        ...prev,
        { role: "user", text: userLine.trim() || "[Files only]" },
        ...(willUseWs ? [{ role: "thinking_pending" }] : []),
      ]);
      setBusy(true);

      if (willUseWs) {
        const conversationHistory = compactConversationHistoryForWire(messages);
        const pendingApps = pendingApprovedAppsRef.current || [];
        const confirmBuild =
          pendingApps.length > 0 && looksLikeBuildConfirmation(userText);
        const payload = {
          action: "generate_agent_config",
          instructions: userText || "Use the attached reference files to propose or refine this agent.",
          conversationHistory,
          attachments: attPayload,
          token: localStorage.getItem("tok_") || undefined,
          user_id: localStorage.getItem("user") || undefined,
          tenant_id: localStorage.getItem("ten_") || localStorage.getItem("tenant") || undefined,
          currentAgent: compactCurrentAgentForWire(currentAgent),
        };
        if (confirmBuild) {
          payload.buildConfirmed = true;
          payload.approved_apps = pendingApps;
        } else if (pendingApps.length > 0) {
          // Still clarifying — let intake see proposed apps from prior turn
          payload.approved_apps = pendingApps;
        }
        try {
          wsRef.current.send(JSON.stringify(payload));
        } catch (err) {
          setBusy(false);
          setMessages((prev) => [
            ...stripCopilotWaitTail(prev),
            { role: "assistant", text: `Send failed: ${err?.message || err}` },
          ]);
        }
        return;
      }

      let merged = userText;
      attPayload.forEach((a) => {
        merged += `\n\n--- ${a.name} ---\n${a.content}`;
      });
      const fallback = heuristicProposal(merged || userText, currentAgent);
      setDraft(fallback);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text: "Generated a local draft proposal (WebSocket unavailable).",
        },
      ]);
      setBusy(false);
    },
    [connected, currentAgent, messages],
  );

  useImperativeHandle(
    ref,
    () => ({
      /**
       * Regenerate watches/actions/rules from the latest instructions.
       * Caller must merge drafts without dropping existing actions.
       */
      refineFromInstructions: (instructionsRaw) => {
        const instructions = String(instructionsRaw || "").trim();
        if (!instructions) return false;
        const wsReady = Boolean(connected && wsRef.current && wsRef.current.readyState === WebSocket.OPEN);
        // Prefer live copilot; if WS is configured but not ready yet, let the caller retry.
        if (wsEndpoint && !wsReady) return false;
        const prompt =
          "Update this agent's configuration to match the instructions below.\n\n" +
          "EVALUATE FIRST (required):\n" +
          "- Decide whether the existing tools already satisfy the instructions.\n" +
          "- If they do: only update rules / field values / schedule / labels. Do NOT search for new apps, crawl API docs, or invent tools.\n" +
          "- Only request new tools/apps when the instructions clearly need a capability the current toolset lacks.\n\n" +
          "CRITICAL CONSTRAINTS:\n" +
          "- Do NOT remove any existing actions/tools. Only ADD new actions if they are clearly needed.\n" +
          "- Prefer keeping the current trigger/watch; only change it if the instructions clearly require a different event.\n" +
          "- Update or extend rules so they reference the full action set (including any actions you add).\n" +
          "- Do not replace or rewrite the user's Instructions/goal text.\n\n" +
          `Instructions:\n${instructions}`;
        runAgentBuilderSend(prompt, []);
        return true;
      },
      isBusy: () => busy,
      isConnected: () =>
        Boolean(connected && wsRef.current && wsRef.current.readyState === WebSocket.OPEN),
    }),
    [busy, connected, runAgentBuilderSend, wsEndpoint],
  );

  const submit = () => {
    const userText = input.trim();
    if (!userText && attachments.length === 0) return;
    const attPayload = trimAttachmentsForPayload(attachments);
    runAgentBuilderSend(userText, attPayload);
    setInput("");
    setAttachments([]);
  };

  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  /** After "Start building" from New agent modal: send initial prompt once WS is ready (no extra click). */
  useEffect(() => {
    const text = (initialPrompt || "").trim();
    if (!text) return;
    const sendKey = initialPromptAutoSendKey(copilotStorageId, text);
    if (initialPromptAutoSentRef.current === text || autoSentInitialPrompts.has(sendKey)) {
      onInitialPromptConsumed?.();
      return;
    }
    // Remount after a prior send: persisted thread already has this user message.
    const alreadyInThread = (messagesRef.current || []).some(
      (m) => m?.role === "user" && String(m.text || "").trim() === text,
    );
    if (alreadyInThread) {
      initialPromptAutoSentRef.current = text;
      autoSentInitialPrompts.add(sendKey);
      onInitialPromptConsumed?.();
      return;
    }
    if (busy) return;
    if (wsEndpoint) {
      if (!connected || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) return;
    }
    initialPromptAutoSentRef.current = text;
    autoSentInitialPrompts.add(sendKey);
    runAgentBuilderSend(text, []);
    setInput("");
    setAttachments([]);
    onInitialPromptConsumed?.();
  }, [
    initialPrompt,
    connected,
    busy,
    wsEndpoint,
    runAgentBuilderSend,
    copilotStorageId,
    onInitialPromptConsumed,
  ]);

  useEffect(() => {
    if (draft && onApplyDraft) {
      onApplyDraft(draft);
    }
  }, [draft]);

  useLayoutEffect(() => {
    const el = threadScrollRef.current;
    if (!el) return;
    const apply = () => {
      const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
      const nearBottom = distance < 120;
      if (stickToBottomRef.current || nearBottom) {
        el.scrollTop = el.scrollHeight;
      }
      stickToBottomRef.current = false;
    };
    apply();
    requestAnimationFrame(apply);
  }, [messages, draft]);

  useLayoutEffect(() => {
    const el = thinkingStreamScrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  const composerBlock = (
    <Box
      flexShrink={0}
      pt={unifiedDock ? 1 : 1}
      px={unifiedDock ? 1 : 0.25}
      pb={unifiedDock ? 1 : 0.5}
      style={{
        borderTop: unifiedDock && dockChrome ? dockChrome.composerBorderTop : "1px solid rgba(0,0,0,0.08)",
        backgroundColor: unifiedDock && dockChrome ? dockChrome.composerBg : "#fff",
      }}
    >
      {attachments.length > 0 ? (
        <Box display="flex" flexWrap="wrap" style={{ gap: 6, marginBottom: 6 }}>
          {attachments.map((a) => (
            <Chip
              key={a.id}
              size="small"
              label={a.name}
              onDelete={() => setAttachments((prev) => prev.filter((x) => x.id !== a.id))}
              deleteIcon={<CloseIcon style={{ fontSize: 16 }} />}
            />
          ))}
        </Box>
      ) : null}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: "none" }}
        accept=".txt,.md,.csv,.json,.yaml,.yml,.xml,.log,.tsv,.html,.htm,.css,.js,.jsx,.ts,.tsx,.pdf,.png,.jpg,.jpeg,.gif,.webp"
        onChange={handleFileInputChange}
      />
      <Box
        style={{
          border: unifiedDock && dockChrome ? dockChrome.inputShellBorder : "1px solid rgba(0, 0, 0, 0.23)",
          borderRadius: unifiedDock ? 12 : 8,
          backgroundColor: unifiedDock && dockChrome ? dockChrome.inputShellBg : "#fff",
          overflow: "hidden",
        }}
      >
        <textarea
          value={input}
          onChange={(ev) => setInput(ev.target.value)}
          onKeyDown={(ev) => {
            if (ev.key === "Enter" && (ev.metaKey || ev.ctrlKey)) {
              ev.preventDefault();
              submit();
            }
          }}
          placeholder={
            unifiedDock
              ? "Message the copilot… (⌘/Ctrl+Enter to send)"
              : "Describe changes… (⌘/Ctrl+Enter). Long text scrolls here."
          }
          disabled={busy}
          className={unifiedDock ? unifiedTextareaClasses.textarea : undefined}
          style={{
            display: "block",
            width: "100%",
            minHeight: COMPOSER_TEXTAREA_MIN_PX,
            maxHeight: `min(${COMPOSER_TEXTAREA_MAX_VH}vh, 520px)`,
            height: `clamp(${COMPOSER_TEXTAREA_MIN_PX}px, 22vh, min(${COMPOSER_TEXTAREA_MAX_VH}vh, 520px))`,
            padding: "10px 12px",
            border: "none",
            outline: "none",
            resize: "none",
            boxSizing: "border-box",
            fontFamily: "inherit",
            fontSize: 14,
            lineHeight: 1.45,
            overflowY: "auto",
            overflowX: "hidden",
            background: "transparent",
          }}
        />
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexShrink={0}
          style={{
            height: COMPOSER_TOOLBAR_HEIGHT,
            minHeight: COMPOSER_TOOLBAR_HEIGHT,
            borderTop: unifiedDock && dockChrome ? dockChrome.toolbarBorderTop : "1px solid rgba(0,0,0,0.08)",
            paddingLeft: 2,
            paddingRight: 2,
            backgroundColor: unifiedDock && dockChrome ? dockChrome.toolbarBg : "#fafafa",
          }}
        >
          <Tooltip title="Attach files — text, code, PDF, images (context for the copilot)" placement="top">
            <span>
              <IconButton
                size="small"
                aria-label="Attach files"
                onClick={() => fileInputRef.current?.click()}
                disabled={busy}
                style={{
                  backgroundColor: unifiedDock && dockChrome ? dockChrome.attachBtnBg : "rgba(0,0,0,0.06)",
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Send message to draft or update the agent (⌘ or Ctrl + Enter)" placement="top">
            <span>
              <IconButton
                color="primary"
                size="small"
                aria-label="Send"
                onClick={submit}
                disabled={busy || (!input.trim() && attachments.length === 0)}
                style={{
                  backgroundColor: busy
                    ? theme.palette.action.disabledBackground
                    : theme.palette.primary.main,
                  color: busy
                    ? theme.palette.action.disabled
                    : theme.palette.getContrastText(theme.palette.primary.main),
                  padding: 8,
                }}
              >
                {busy ? (
                  <CircularProgress size={22} style={{ color: theme.palette.text.secondary }} />
                ) : (
                  <KeyboardArrowUpIcon />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );

  const threadScrollStyle = unifiedDock
    ? {
        padding: "10px 8px 12px",
        backgroundColor: dockChrome.threadBg,
        WebkitOverflowScrolling: "touch",
      }
    : { paddingRight: 4, WebkitOverflowScrolling: "touch" };

  return (
    <Box
      display="flex"
      flexDirection="column"
      flex={1}
      minHeight={0}
      minWidth={0}
      width="100%"
      overflow="hidden"
      style={{ flexBasis: 0, flexGrow: 1 }}
    >
      <Box
        ref={threadScrollRef}
        flex="1 1 0"
        minHeight={0}
        minWidth={0}
        overflow="auto"
        data-copilot-thread="1"
        style={{
          ...threadScrollStyle,
          flexBasis: 0,
          WebkitOverflowScrolling: "touch",
          overscrollBehavior: "contain",
        }}
      >
        {messages.length === 0 && !draft ? (
          <Typography
            variant="body2"
            color="textSecondary"
            align={unifiedDock ? "center" : "inherit"}
            style={{ margin: unifiedDock ? "24px 12px" : "0 0 12px 0", opacity: 0.85 }}
          >
            {unifiedDock
              ? "No messages yet — type below to draft or refine this agent."
              : "Describe changes or click Draft to regenerate from your instructions below."}
          </Typography>
        ) : null}
        {messages.map((item, index) =>
          unifiedDock && item.role === "thinking_pending" ? (
            <Box key={`thinking_pending_${index}`} display="flex" justifyContent="flex-start" mb={1} px={0.5}>
              <Box
                style={{
                  maxWidth: "95%",
                  padding: "8px 12px",
                  borderRadius: 10,
                  backgroundColor: dockChrome.thinkingInlineBg,
                  border: dockChrome.thinkingInlineBorder,
                }}
              >
                <Typography
                  variant="caption"
                  style={{
                    color: dockChrome.thinkingCaptionColor,
                    fontWeight: 600,
                    display: "block",
                    marginBottom: 4,
                  }}
                >
                  Copilot
                </Typography>
                <Typography variant="body2" className={thinkingPulseClasses.pulse}>
                  Planning next moves…
                </Typography>
              </Box>
            </Box>
          ) : unifiedDock && item.role === "thinking_stream" ? (
            <Box key={`thinking_stream_${index}`} mb={1.25} px={0.5}>
              <Typography
                variant="caption"
                style={{
                  color: dockChrome.thinkingCaptionColor,
                  fontWeight: 600,
                  marginBottom: 6,
                  display: "block",
                }}
              >
                Copilot
                {!item.streamComplete ? " · planning" : ""}
              </Typography>
              {item.reasoningFull && item.streamComplete ? (
                <Button
                  size="small"
                  onClick={() =>
                    setMessages((prev) =>
                      prev.map((m, i) =>
                        i === index && m.role === "thinking_stream"
                          ? { ...m, thinkingDetailsOpen: !m.thinkingDetailsOpen }
                          : m
                      )
                    )
                  }
                  style={{ marginBottom: 8, textTransform: "none", padding: "2px 8px" }}
                >
                  {item.thinkingDetailsOpen ? "Hide technical trace" : "Show technical trace"}
                </Button>
              ) : null}
              <Box
                ref={index === messages.length - 1 ? thinkingStreamScrollRef : undefined}
                style={{
                  maxHeight: 240,
                  overflowY: "auto",
                  overflowX: "hidden",
                  backgroundColor: dockChrome.thinkingPanelBg,
                  border: dockChrome.thinkingPanelBorder,
                  borderRadius: 10,
                  padding: "10px 12px",
                }}
              >
                {item.reasoning || item.reasoningFull ? (
                  <Typography
                    component="div"
                    style={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      fontSize: 13,
                      lineHeight: 1.55,
                      color: dockChrome.thinkingBodyColor,
                      fontFamily: 'ui-sans-serif, system-ui, "Segoe UI", Roboto, sans-serif',
                    }}
                  >
                    {item.thinkingDetailsOpen && item.reasoningFull ? item.reasoningFull : item.reasoning}
                  </Typography>
                ) : null}
                {item.output ? (
                  <Typography
                    component="div"
                    style={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      fontSize: 11,
                      lineHeight: 1.45,
                      color: dockChrome.thinkingOutputColor,
                      marginTop: item.reasoning ? 10 : 0,
                    }}
                  >
                    {item.output}
                  </Typography>
                ) : null}
                {!item.reasoning && !item.output && !item.streamComplete ? (
                  <Typography variant="body2" className={thinkingPulseClasses.pulse}>
                    Planning next moves…
                  </Typography>
                ) : null}
                <ResearchSourceChips sources={item.sources} />
              </Box>
            </Box>
          ) : unifiedDock && item.role === "thinking" ? (
            <Box key={`thinking_${index}`} display="flex" justifyContent="flex-start" mb={1} px={0.5}>
              <Box
                style={{
                  maxWidth: "95%",
                  padding: "8px 12px",
                  borderRadius: 10,
                  backgroundColor: dockChrome.thinkingInlineBg,
                  border: dockChrome.thinkingInlineBorder,
                }}
              >
                <Typography
                  variant="caption"
                  style={{
                    color: dockChrome.thinkingCaptionColor,
                    fontWeight: 600,
                    display: "block",
                    marginBottom: 4,
                  }}
                >
                  Copilot
                </Typography>
                <Typography
                  variant="body2"
                  style={{
                    color: dockChrome.thinkingCaptionColor,
                    fontStyle: "italic",
                    lineHeight: 1.45,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {item.text}
                </Typography>
              </Box>
            </Box>
          ) : unifiedDock ? (
            <Box
              key={`${item.role}_${index}`}
              display="flex"
              justifyContent={item.role === "user" ? "flex-end" : "flex-start"}
              mb={1.25}
            >
              <Box
                style={{
                  maxWidth: "min(88%, 100%)",
                  minWidth: 0,
                  padding: "10px 14px",
                  borderRadius: 16,
                  backgroundColor:
                    item.role === "user" ? theme.palette.primary.main : dockChrome.assistantBubbleBg,
                  color:
                    item.role === "user"
                      ? theme.palette.getContrastText(theme.palette.primary.main)
                      : dockChrome.assistantBubbleColor,
                  boxShadow: item.role === "assistant" ? dockChrome.assistantBubbleShadow : "none",
                  border: item.role === "assistant" ? dockChrome.assistantBubbleBorder : "none",
                  overflow: "visible",
                }}
              >
                <Typography
                  variant="caption"
                  style={{
                    display: "block",
                    marginBottom: 4,
                    opacity: item.role === "user" ? 0.9 : 0.65,
                    fontWeight: 600,
                  }}
                >
                  {item.role === "user" ? "You" : "Assistant"}
                </Typography>
                <Typography variant="body2" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.45, overflowWrap: "anywhere" }}>
                  {item.text}
                </Typography>
              </Box>
            </Box>
          ) : item.role === "thinking_pending" ? (
            <Box key={`thinking_pending_${index}`} mb={1.25}>
              <Typography variant="caption" color="textSecondary">
                Copilot
              </Typography>
              <Typography variant="body2" className={thinkingPulseClasses.pulse}>
                Planning next moves…
              </Typography>
            </Box>
          ) : item.role === "thinking_stream" ? (
            <Box key={`ts_${index}`} mb={1.25}>
              <Typography variant="caption" color="textSecondary">
                Copilot {item.streamComplete ? "" : "(planning…)"}
              </Typography>
              {item.reasoningFull && item.streamComplete ? (
                <Button
                  size="small"
                  onClick={() =>
                    setMessages((prev) =>
                      prev.map((m, i) =>
                        i === index && m.role === "thinking_stream"
                          ? { ...m, thinkingDetailsOpen: !m.thinkingDetailsOpen }
                          : m
                      )
                    )
                  }
                  style={{ marginTop: 4, marginBottom: 4, textTransform: "none", padding: "2px 8px" }}
                >
                  {item.thinkingDetailsOpen ? "Hide technical trace" : "Show technical trace"}
                </Button>
              ) : null}
              <Box
                style={{
                  maxHeight: 200,
                  overflowY: "auto",
                  padding: 8,
                  backgroundColor: "rgba(0,0,0,0.04)",
                  borderRadius: 8,
                  marginTop: 4,
                }}
              >
                {item.reasoning || item.reasoningFull ? (
                  <Typography component="pre" style={{ whiteSpace: "pre-wrap", fontSize: 12, margin: 0 }}>
                    {item.thinkingDetailsOpen && item.reasoningFull ? item.reasoningFull : item.reasoning}
                  </Typography>
                ) : null}
                {item.output ? (
                  <Typography component="pre" style={{ whiteSpace: "pre-wrap", fontSize: 11, marginTop: 8, margin: 0, color: "#666" }}>
                    {item.output}
                  </Typography>
                ) : null}
                {!item.reasoning && !item.output && !item.streamComplete ? (
                  <Typography variant="body2" className={thinkingPulseClasses.pulse} style={{ margin: 0 }}>
                    Planning next moves…
                  </Typography>
                ) : null}
                <ResearchSourceChips sources={item.sources} />
              </Box>
            </Box>
          ) : item.role === "thinking" ? (
            <Box key={`thinking_${index}`} mb={1.25}>
              <Typography variant="caption" color="textSecondary">
                Copilot
              </Typography>
              <Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {item.text}
              </Typography>
            </Box>
          ) : (
            <Box key={`${item.role}_${index}`} mb={1.25}>
              <Typography variant="caption" color="textSecondary">
                {item.role === "user" ? "You" : "Assistant"}
              </Typography>
              <Typography variant="body2" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {item.text}
              </Typography>
            </Box>
          )
        )}
        {draft ? (
          unifiedDock ? (
            <Box display="flex" justifyContent="flex-start" mb={1}>
              <Box
                style={{
                  maxWidth: "92%",
                  padding: "10px 14px",
                  borderRadius: 16,
                  backgroundColor: dockChrome.draftCardBg,
                  border: dockChrome.draftCardBorder,
                  boxShadow: dockChrome.draftCardShadow,
                }}
              >
                <Typography
                  variant="caption"
                  style={{
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    display: "block",
                    marginBottom: 6,
                  }}
                >
                  Latest draft applied
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ lineHeight: 1.45 }}>
                  Triggers: {draft.triggers?.length || 0} · Actions: {draft.actions?.length || 0} · Rules:{" "}
                  {draft.rules?.length || 0}
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 6 }}>
                  Instructions in the goal section are yours only — the copilot does not change them.
                </Typography>
                <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
                  Saved to the form automatically.
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box mt={1} pt={1.5} style={{ borderTop: "1px solid rgba(0,0,0,0.08)" }}>
              <Typography variant="subtitle2" gutterBottom>
                Last proposal
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Triggers: {draft.triggers?.length || 0} | Actions: {draft.actions?.length || 0} | Rules:{" "}
                {draft.rules?.length || 0}
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 6 }}>
                Your goal / instructions are not modified by the copilot.
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
                Applied and saved automatically.
              </Typography>
            </Box>
          )
        ) : null}
      </Box>
      {composerBlock}
    </Box>
  );
});

AgentDraftingAssistant.displayName = "AgentDraftingAssistant";

export default AgentDraftingAssistant;
